import type { FC } from 'react'

import type { ValueLabelPair } from '@skytech/ui'
import { Input, InputWithValidation, Label, Select } from '@skytech/ui'
import { COUNTRY_FLAGS, countryOptions, isOnlyLetters, isPostcodeInRange } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { Product } from '@/modules/ecom-policy/types'
import employeeResources from '@/resources/employees'
import { commonQueries } from '@/resources/queries'

import './section-address.scss'

const CUSTOM_ADDRESS_ID = -1

function getTransportationProduct({
  transportationProducts,
  selectedPostcode,
}: {
  transportationProducts: Product[]
  selectedPostcode: string
}) {
  return transportationProducts.find(product =>
    isPostcodeInRange({ postcode: selectedPostcode, rangeString: product.name })
  )
}

const SectionAddress: FC = () => {
  const translate = useTranslation()
  const isAdmin = useIsAdmin()
  const shopCtx = useShopNewOrderContext()
  const [selectedAddressId, setSelectAddressId] = shopCtx.selectedAddressId
  const [customAddress, setCustomAddress] = shopCtx.customAddress
  const [costObjectId] = shopCtx.costObjectId
  const [contactName, setContactName] = shopCtx.contactName
  const [contactNumber, setContactNumber] = shopCtx.contactNumber
  const [transportationProduct, setTransportationProduct] = shopCtx.transportationProduct
  const [productQuantityById, setProductQuantityById] = shopCtx.productQuantityById
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  if (!selectedPolicyId) throw Error('No policy selected!')

  const { data: policyData } = useQuery(queries.policyDetails(selectedPolicyId), () =>
    ecomPolicyResources.getPolicyDetails(selectedPolicyId)
  )

  const { data } = useQuery(
    queries.shopAddressesSetup(),
    () => {
      if (!policyData?.vendorId) throw Error('Policy data is missing!')
      return ecomPolicyResources.getShopAddressSetup(policyData.vendorId)
    },
    {
      enabled:
        Boolean(selectedPolicyId) &&
        Boolean(costObjectId) &&
        Boolean(policyData) &&
        Boolean(policyData?.vendorId),
    }
  )

  const { data: policyProducts } = useQuery(queries.policyProducts(selectedPolicyId), () =>
    ecomPolicyResources.getPolicyProducts(selectedPolicyId)
  )
  const { data: employeeData } = useQuery(commonQueries.employees(), () =>
    employeeResources.getList(isAdmin)
  )
  const selectedLocationCostObjectId = employeeData?.locationList.find(
    location => location.id === costObjectId
  )

  if (!data) return null

  const isAddressSelectVisible = data?.length > 0
  const isAddressInputsVisible = !isAddressSelectVisible || selectedAddressId === CUSTOM_ADDRESS_ID

  const addressOptions = [
    ...data.map(address => ({
      value: address.id,
      label: `${address.address}, ${address.zip} ${address.city}`,
    })),
  ]

  if (policyData?.isCustomAddressActivated) {
    addressOptions.push({
      value: CUSTOM_ADDRESS_ID,
      label: translate('Custom address'),
    })
  }

  function handleSelectTransportationProduct(postcode: string) {
    const selectedTransportationProduct = getTransportationProduct({
      selectedPostcode: postcode,
      transportationProducts: policyProducts?.transportationProducts || [],
    })
    setTransportationProduct(selectedTransportationProduct || null)
    // Clean up
    if (transportationProduct) {
      setProductQuantityById({ ...productQuantityById, [transportationProduct.id]: 0 })
    }
    if (selectedTransportationProduct) {
      setProductQuantityById({ ...productQuantityById, [selectedTransportationProduct.id]: 1 })
    }
  }

  const handleSelectAddress = (option: ValueLabelPair | null) => {
    if (!option) throw Error('No option selected!')
    if (typeof option.value === 'string') throw Error('Incorrect address id!')
    setSelectAddressId(option.value)
    if (option.value === CUSTOM_ADDRESS_ID) {
      if (customAddress?.zip) handleSelectTransportationProduct(customAddress?.zip)
      return
    }
    const postcode = data.find(address => address.id === option.value)?.zip
    if (!postcode) throw Error('Postcode not found!')
    handleSelectTransportationProduct(postcode)
  }

  return (
    <section className="p-4">
      {isAddressSelectVisible && (
        <Select
          id="delivery-address"
          label={<Translation id="Delivery address" />}
          placeholder={translate('Select delivery address')}
          className="react-select"
          options={addressOptions}
          onChange={handleSelectAddress}
          value={selectedAddressId || undefined}
        />
      )}

      <div
        className={`step-confirm__address__inputs ${
          isAddressInputsVisible ? 'is-visible' : 'is-hidden'
        } my-2 gap-2`}
      >
        <div className="adr">
          <Input
            id="address"
            label={<Translation id="Address" />}
            value={customAddress?.address || ''}
            onChange={e => setCustomAddress({ ...(customAddress || {}), address: e.target.value })}
          />
        </div>

        <div className="zip">
          <Input
            id="zip"
            label={<Translation id="Zip" />}
            value={customAddress?.zip || ''}
            maxLength={10}
            onChange={e => {
              setCustomAddress({ ...(customAddress || {}), zip: e.target.value })
              handleSelectTransportationProduct(e.target.value)
            }}
          />
        </div>

        <div className="city">
          <InputWithValidation
            id="city"
            errorMessage={<Translation id="City name may only include letters" />}
            validator={isOnlyLetters}
            label={<Translation id="City" />}
            value={customAddress?.city || ''}
            onChange={e => setCustomAddress({ ...(customAddress || {}), city: e.target.value })}
          />
        </div>
      </div>

      {Boolean(selectedLocationCostObjectId) && (
        <>
          <Input
            id="contactName"
            isFullWidth
            label={
              <>
                <Translation id="Contact name" />*
              </>
            }
            value={contactName || ''}
            onChange={e => setContactName(e.target.value)}
          />

          <div className="mt-2">
            <Label>
              <Translation id="Contact number" />*
            </Label>

            <div className="flex items-center gap-2">
              <Select
                value={contactNumber.phoneCountryCode || undefined}
                id="phoneCountryId"
                options={countryOptions.map(option => ({
                  value: option.value,
                  label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                }))}
                className="w-fit"
                marginBottom={16}
                onChange={option =>
                  setContactNumber({
                    phoneNumber: contactNumber?.phoneNumber || '',
                    phoneCountryCode: option?.value || null,
                  })
                }
              />

              <Input
                isFullWidth
                id="contactNumber"
                value={contactNumber?.phoneNumber || ''}
                marginBottom={16}
                wrapperClassName="grow"
                onChange={e =>
                  setContactNumber({
                    phoneCountryCode: contactNumber?.phoneCountryCode || null,
                    phoneNumber: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </>
      )}
    </section>
  )
}

export default SectionAddress
